import fixtureInput from './vs08-r2-pc-demo.json';

export type DemoId = `DEMO-${string}`;
export interface DemoPoint {
  x: number;
  y: number;
}
export interface DemoRegion {
  id: DemoId;
  name: string;
  polygon: [number, number][];
  border: 'warning' | 'watch';
}
export interface DemoMonitoringItem {
  id: DemoId;
  kind: 'OBSERVATION_WARNING' | 'FORECAST_WATCH';
  title: string;
  description: string;
  time: string;
  point: DemoPoint;
  regionId: DemoId;
  detail: string;
  changedAt: string;
  pendingEvaluation?: boolean;
}
export interface DemoSatelliteItem {
  id: DemoId;
  kind: 'SATELLITE_SAMPLE';
  title: string;
  time: string;
  point: DemoPoint;
  frpMw: number;
  brightnessK: number;
  nativeConfidence: string;
  linkedWeather: boolean;
  weatherDistanceKm: number | null;
}
export type DemoItem = DemoMonitoringItem | DemoSatelliteItem;
export interface DemoFixture {
  schemaVersion: 'VS08_R2_PC_DEMO_V1';
  sourceAuthority: 'DEMO_FIXTURE_ONLY';
  notice: string;
  map: {
    mode: 'ILLUSTRATIVE_IMAGE_COORDINATES_ONLY';
    projection: null;
    locationAuthority: 'NOT_GEOREFERENCED';
    positionUnit: 'relative_0_to_1';
    image: string;
    reference: string;
    defaultRegion: string;
    attribution: string;
  };
  monitoring: {
    label: string;
    status: 'SYNTHETIC_NON_OPERATIONAL';
    summary: {
      observationWarnings: number;
      forecastWatches: number;
      sourcesHealthy: number;
      sourcesTotal: number;
      pendingForecastEvaluations: number;
    };
    regions: DemoRegion[];
    items: DemoMonitoringItem[];
    sources: {
      id: DemoId;
      name: string;
      point: DemoPoint | null;
      state: 'HEALTHY' | 'STALE' | 'DISABLED';
    }[];
  };
  historicalResearch: {
    label: string;
    status: 'DEMO_HISTORICAL_NOT_VERIFIED';
    date: string;
    eventGroundTruth: 'NOT_VERIFIED';
    summary: {
      satelliteCandidateCount: number;
      regionalWeatherContextCount: number;
      withoutWeatherContextCount: number;
      verifiedFireEvents: number;
    };
    items: DemoSatelliteItem[];
  };
}
const object = (value: unknown): Record<string, unknown> => {
  if (!value || typeof value !== 'object' || Array.isArray(value))
    throw new Error('DEMO fixture: object required');
  return value as Record<string, unknown>;
};
const list = (value: unknown): unknown[] => {
  if (!Array.isArray(value)) throw new Error('DEMO fixture: array required');
  return value;
};
const assert = (value: boolean, message: string) => {
  if (!value) throw new Error(`DEMO fixture: ${message}`);
};
const string = (value: unknown) => typeof value === 'string' && value.length > 0;
const number = (value: unknown) =>
  typeof value === 'number' && Number.isFinite(value) && value >= 0;
const relative = (value: unknown) => number(value) && (value as number) <= 1;
const member = (value: unknown, allowed: readonly string[]): value is string =>
  typeof value === 'string' && allowed.includes(value);
function point(value: unknown) {
  const p = object(value);
  assert(relative(p.x) && relative(p.y), 'relative point out of bounds');
}
/** Fail closed before rendering. Counts derive from the records, never from a LIVE DTO. */
export function validateDemoFixture(value: unknown): DemoFixture {
  const data = object(value),
    map = object(data.map),
    monitor = object(data.monitoring),
    historical = object(data.historicalResearch);
  assert(
    data.schemaVersion === 'VS08_R2_PC_DEMO_V1' &&
      data.sourceAuthority === 'DEMO_FIXTURE_ONLY' &&
      string(data.notice),
    'authority/schema mismatch',
  );
  assert(
    map.mode === 'ILLUSTRATIVE_IMAGE_COORDINATES_ONLY' &&
      map.projection === null &&
      map.locationAuthority === 'NOT_GEOREFERENCED' &&
      map.positionUnit === 'relative_0_to_1',
    'map authority mismatch',
  );
  for (const key of ['image', 'reference', 'defaultRegion', 'attribution'])
    assert(string(map[key]), `map ${key}`);
  assert(
    monitor.status === 'SYNTHETIC_NON_OPERATIONAL' &&
      historical.status === 'DEMO_HISTORICAL_NOT_VERIFIED' &&
      historical.eventGroundTruth === 'NOT_VERIFIED' &&
      string(monitor.label) &&
      string(historical.label) &&
      string(historical.date),
    'namespace authority mismatch',
  );
  const ids = new Set<string>();
  const identify = (entry: Record<string, unknown>) => {
    assert(
      typeof entry.id === 'string' && entry.id.startsWith('DEMO-') && !ids.has(entry.id),
      'invalid/duplicate DEMO ID',
    );
    ids.add(entry.id as string);
  };
  const regions = list(monitor.regions).map(object);
  for (const region of regions) {
    identify(region);
    assert(string(region.name) && member(region.border, ['warning', 'watch']), 'region fields');
    const polygon = list(region.polygon);
    assert(polygon.length >= 3, 'polygon requires three vertices');
    for (const vertex of polygon) {
      const pair = list(vertex);
      assert(pair.length === 2 && pair.every(relative), 'polygon bounds');
    }
    const vertices = polygon as number[][];
    const area = vertices.reduce((sum, p, i) => {
      const next = vertices[(i + 1) % vertices.length]!;
      return sum + p[0]! * next[1]! - next[0]! * p[1]!;
    }, 0);
    assert(Math.abs(area) > 0.00001, 'polygon has no area');
  }
  const items = list(monitor.items).map(object),
    sources = list(monitor.sources).map(object),
    satellites = list(historical.items).map(object);
  for (const item of items) {
    identify(item);
    point(item.point);
    assert(
      member(item.kind, ['OBSERVATION_WARNING', 'FORECAST_WATCH']) &&
        regions.some((r) => r.id === item.regionId),
      'monitoring kind/region',
    );
    for (const key of ['title', 'description', 'time', 'detail', 'changedAt'])
      assert(string(item[key]), `monitoring ${key}`);
    assert(
      Number.isFinite(Date.parse(String(item.changedAt))) &&
        (item.pendingEvaluation === undefined || typeof item.pendingEvaluation === 'boolean'),
      'monitoring evaluation/time',
    );
  }
  for (const source of sources) {
    identify(source);
    assert(
      string(source.name) && member(source.state, ['HEALTHY', 'STALE', 'DISABLED']),
      'source fields',
    );
    if (source.point !== null) point(source.point);
  }
  for (const item of satellites) {
    identify(item);
    point(item.point);
    assert(
      item.kind === 'SATELLITE_SAMPLE' &&
        string(item.title) &&
        string(item.time) &&
        number(item.frpMw) &&
        number(item.brightnessK) &&
        member(item.nativeConfidence, ['h', 'n', 'l']) &&
        typeof item.linkedWeather === 'boolean',
      'satellite fields',
    );
    assert(
      item.linkedWeather ? number(item.weatherDistanceKm) : item.weatherDistanceKm === null,
      'weather relation',
    );
  }
  const summary = object(monitor.summary),
    research = object(historical.summary);
  assert(
    summary.observationWarnings === items.filter((i) => i.kind === 'OBSERVATION_WARNING').length &&
      summary.forecastWatches === items.filter((i) => i.kind === 'FORECAST_WATCH').length &&
      summary.sourcesTotal === sources.length &&
      summary.sourcesHealthy === sources.filter((s) => s.state === 'HEALTHY').length &&
      summary.pendingForecastEvaluations ===
        items.filter((i) => i.kind === 'FORECAST_WATCH' && i.pendingEvaluation === true).length,
    'monitoring summary mismatch',
  );
  assert(
    research.satelliteCandidateCount === satellites.length &&
      research.regionalWeatherContextCount === satellites.filter((i) => i.linkedWeather).length &&
      research.withoutWeatherContextCount === satellites.filter((i) => !i.linkedWeather).length &&
      research.verifiedFireEvents === 0,
    'historical summary mismatch',
  );
  return value as DemoFixture;
}
export const demoFixture = validateDemoFixture(fixtureInput);
export function demoKind(item: DemoItem): 'warning' | 'forecast' | 'linked' | 'unlinked' {
  return item.kind === 'SATELLITE_SAMPLE'
    ? item.linkedWeather
      ? 'linked'
      : 'unlinked'
    : item.kind === 'OBSERVATION_WARNING'
      ? 'warning'
      : 'forecast';
}
